import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { LevelUpModal } from '../LevelUpModal'
import { LevelRewardModal } from '../LevelRewardModal'
import {
  claimUserLevelReward,
  getUserLevelRewards,
  type LevelRewardBundle,
  type LevelRewardLine,
} from '../../common/api'
import { getResources } from '../../store/user/actions'
import {
  MilestoneCelebrationModal,
  MilestoneVariant,
  MILESTONE_CASTLE_UNLOCK_LEVEL,
} from '../MilestoneCelebrationModal'

/** Account (user) level-ups are handled by LevelRewardModal, not by this toast. */
interface LevelUpData {
  type: 'character' | 'kingdom'
  level: number
  characterName?: string
  characterImage?: string
  characterId?: string
}

// Helper function to convert Number objects to primitive numbers
const toNumber = (value: any): number => {
  if (value === null || value === undefined) return 0
  if (typeof value === 'number') return value
  const num = Number(value)
  return isNaN(num) ? 0 : num
}

const LevelUpTracker: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const user = useSelector((state: RootState) => state.userModule.user)
  const userCharacters = useSelector((state: RootState) => state.app.game.userCharacters || [])
  const empirePeriod = useSelector((state: RootState) => state.app.land.empirePeriod)

  // Track previous levels to detect changes
  const prevUserLevelRef = useRef<number | null>(null)
  const prevCharacterLevelsRef = useRef<Map<string, number>>(new Map())
  const prevKingdomLevelRef = useRef<number | null>(null)
  const isInitializedRef = useRef<boolean>(false)
  const hasUserDataRef = useRef<boolean>(false)
  const hasCharactersDataRef = useRef<boolean>(false)
  const hasKingdomDataRef = useRef<boolean>(false)
  const userLevelSyncedRef = useRef<boolean>(false) // Track if we've synced user level at least once
  const hasCheckedUserLevelOnceRef = useRef<boolean>(false) // Track if we've done at least one comparison after init
  const hasCheckedKingdomLevelOnceRef = useRef<boolean>(false) // Track if we've done at least one comparison for kingdom

  // Modal state
  const [levelUpData, setLevelUpData] = useState<LevelUpData | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // MvM milestone celebrations (castle at level 3; shield removal on the first alliance join).
  // Queue so two milestones arriving together show one after the other.
  // Dedup guards against the Redux level-diff and the `user:milestone` socket
  // event both firing for the same milestone in one session.
  const [milestoneQueue, setMilestoneQueue] = useState<MilestoneVariant[]>([])
  const shownMilestonesRef = useRef<Set<MilestoneVariant>>(new Set())

  /**
   * Account level-up rewards, oldest unclaimed level first. Sourced from the server rather
   * than the Redux level diff, so rewards earned while offline (or missed because the tab was
   * closed mid-level-up) still surface on the next load — the modal is a delivery mechanism,
   * not the record of what was earned.
   */
  const [rewardQueue, setRewardQueue] = useState<LevelRewardBundle[]>([])
  const [claimingReward, setClaimingReward] = useState(false)
  /** Concrete rolls returned by the claim; replaces the generic pre-claim descriptions. */
  const [claimedRewardLines, setClaimedRewardLines] = useState<LevelRewardLine[] | null>(null)
  const rewardFetchInFlightRef = useRef(false)
  const walletAddress = user?.walletAddress || ''

  const refreshLevelRewards = useCallback(async (): Promise<boolean> => {
    if (!walletAddress || rewardFetchInFlightRef.current) return false
    rewardFetchInFlightRef.current = true
    try {
      const res = await getUserLevelRewards(walletAddress)
      const claimable = Array.isArray(res?.claimable) ? res.claimable : []
      setRewardQueue((prev) => {
        // Reward-less celebration entries (levels past the table) exist only client-side, so a
        // plain replace would yank an open modal off the screen when any refresh fires.
        const celebrations = prev.filter(
          (b) => b.rewards.length === 0 && !claimable.some((c) => c.level === b.level),
        )
        return [...claimable, ...celebrations]
      })
      return claimable.length > 0
    } catch {
      return false
    } finally {
      rewardFetchInFlightRef.current = false
    }
  }, [walletAddress])

  // Pick up anything earned while away (offline levels, a tab closed mid-level-up).
  useEffect(() => {
    if (!walletAddress) return
    void refreshLevelRewards()
  }, [walletAddress, refreshLevelRewards])

  const enqueueMilestones = useCallback((variants: MilestoneVariant[]) => {
    const fresh = variants.filter((v) => !shownMilestonesRef.current.has(v))
    if (fresh.length === 0) return
    fresh.forEach((v) => shownMilestonesRef.current.add(v))
    setMilestoneQueue((prev) => [...prev, ...fresh])
  }, [])

  /**
   * Milestones crossed by a user level increase prev → curr. The 'shield' milestone is
   * deliberately absent: the starter shield is no longer level-gated, it drops on the first
   * alliance join and only the backend's `user:milestone` event can announce it.
   */
  const milestonesForCrossing = (prev: number, curr: number): MilestoneVariant[] => {
    const crossed: MilestoneVariant[] = []
    if (prev < MILESTONE_CASTLE_UNLOCK_LEVEL && curr >= MILESTONE_CASTLE_UNLOCK_LEVEL) {
      crossed.push('castle')
    }
    return crossed
  }

  // Real-time trigger: backend emits `user:milestone` once per milestone; App.tsx
  // re-dispatches it as a `user-milestone` DOM event (same pattern as diplomacy gifts).
  useEffect(() => {
    const onMilestone = (e: Event) => {
      const milestone = (e as CustomEvent)?.detail?.milestone
      if (milestone === 'mvmCastleUnlocked') enqueueMilestones(['castle'])
      else if (milestone === 'mvmShieldRemoved') enqueueMilestones(['shield'])
      // Backend announces newly-claimable reward levels on the same channel. It never grants
      // here, so a dropped event only delays the modal — the rewards stay claimable.
      else if (milestone === 'levelRewards') void refreshLevelRewards()
    }
    window.addEventListener('user-milestone', onMilestone)
    return () => window.removeEventListener('user-milestone', onMilestone)
  }, [enqueueMilestones, refreshLevelRewards])

  // Initialize previous levels when data becomes available (only once)
  useEffect(() => {
    // Check if we have user data
    const hasUser = user && user.userLvl !== undefined && user.userLvl !== null
    const hasCharacters = Array.isArray(userCharacters) && userCharacters.length > 0

    // Initialize user level (only if not already set and we have valid data)
    // This should only happen once on initial load
    if (hasUser && prevUserLevelRef.current === null && !hasUserDataRef.current) {
      const initialLevel = toNumber(user.userLvl) || 1
      prevUserLevelRef.current = initialLevel
      hasUserDataRef.current = true
      hasCheckedUserLevelOnceRef.current = false // Reset on initialization
      // Set synced flag after a small delay to ensure tracking effect doesn't run immediately
      setTimeout(() => {
        userLevelSyncedRef.current = true
      }, 100)
      // Don't show modal on initial load - this is just setting the baseline
    }

    // Initialize character levels (only if not already initialized and we have valid data)
    // This should only happen once on initial load
    if (hasCharacters && prevCharacterLevelsRef.current.size === 0 && !hasCharactersDataRef.current) {
      const charLevels = new Map<string, number>()
      userCharacters.forEach((char: any) => {
        if (char?._id && char?.level !== undefined && char?.level !== null) {
          charLevels.set(char._id, toNumber(char.level))
        }
      })
      if (charLevels.size > 0) {
        prevCharacterLevelsRef.current = charLevels
        hasCharactersDataRef.current = true
        // Don't show modal on initial load - this is just setting the baseline
      }
    }

    // Initialize kingdom level (only if not already set and we have valid data)
    const hasKingdom = empirePeriod && empirePeriod.palaceLevel !== undefined && empirePeriod.palaceLevel !== null
    if (hasKingdom && prevKingdomLevelRef.current === null && !hasKingdomDataRef.current) {
      const initialLevel = toNumber(empirePeriod.palaceLevel) || 1
      prevKingdomLevelRef.current = initialLevel
      hasKingdomDataRef.current = true
    }

    // Mark as initialized once we have at least some data
    if ((hasUser || hasCharacters || hasKingdom) && !isInitializedRef.current) {
      isInitializedRef.current = true
    }
  }, [user, userCharacters, empirePeriod])

  // Track user level changes
  useEffect(() => {
    // Don't track if not initialized or no user data
    if (!isInitializedRef.current || !user || user.userLvl === undefined || user.userLvl === null) {
      return
    }

    // Don't track if we haven't initialized the previous level yet (first load)
    // This is critical - we must have a baseline before we can detect changes
    if (!hasUserDataRef.current || prevUserLevelRef.current === null || !userLevelSyncedRef.current) {
      return
    }

    const currentUserLevel = toNumber(user.userLvl) || 1
    const prevUserLevel = prevUserLevelRef.current

    // First check after initialization: if levels are equal, it's just initialization, not a level up
    // This prevents false positives on initial load
    if (!hasCheckedUserLevelOnceRef.current) {
      hasCheckedUserLevelOnceRef.current = true
      // If current equals previous, this is initialization - don't show modal, just sync
      if (currentUserLevel === prevUserLevel) {
        // This is the first check and levels match - it's initialization, not a level up
        return // Exit early, don't show modal
      }
      // If they're different on first check, something unexpected happened, but don't show modal
      // Just sync and return
      if (currentUserLevel !== prevUserLevel) {
        prevUserLevelRef.current = currentUserLevel
        return
      }
    }

    // After first check: only show modal if level actually increased (strict comparison)
    // This prevents false positives when user data is refetched with the same level
    if (typeof prevUserLevel === 'number' && currentUserLevel > prevUserLevel) {
      // MvM milestone crossings (castle at 3, shield removal at 5) get their own
      // celebration modal instead of the generic auto-closing level-up toast.
      const crossedMilestones = milestonesForCrossing(prevUserLevel, currentUserLevel)
      if (crossedMilestones.length > 0) {
        enqueueMilestones(crossedMilestones)
        // Milestone levels (3 and 5) also carry rewards; the reward modal renders after the
        // celebration closes, so still refresh the queue here.
        void refreshLevelRewards()
      } else {
        /**
         * One component announces account level-ups: LevelRewardModal. Levels 2–50 come back
         * from the server with a claimable bundle; past the reward table (51+) there is nothing
         * to claim, so queue a reward-less entry and the modal shows the celebration alone.
         */
        void refreshLevelRewards().then((hasRewards) => {
          if (hasRewards) return
          setRewardQueue((prev) =>
            prev.some((b) => b.level === currentUserLevel)
              ? prev
              : [...prev, { level: currentUserLevel, rewards: [] }],
          )
        })
      }
      // Update previous level immediately to prevent duplicate triggers
      prevUserLevelRef.current = currentUserLevel
    } else {
      // Level hasn't changed or decreased - sync the ref silently
      // This handles cases where user data is refetched with the same level
      if (currentUserLevel !== prevUserLevel) {
        prevUserLevelRef.current = currentUserLevel
      }
    }
    // `refreshLevelRewards` is included so the effect never closes over a stale wallet. A
    // re-run without a level change falls through to the silent-sync branch above.
  }, [user?.userLvl, refreshLevelRewards, enqueueMilestones])

  // Track character level changes
  useEffect(() => {
    // Don't track if not initialized or no characters
    if (!isInitializedRef.current || !Array.isArray(userCharacters) || userCharacters.length === 0) {
      return
    }

    // Don't track if we haven't initialized character levels yet (first load)
    if (!hasCharactersDataRef.current) {
      return
    }

    const prevLevels = prevCharacterLevelsRef.current
    const currentLevels = new Map<string, number>()

    // Build current levels map and check for level ups
    userCharacters.forEach((char: any) => {
      if (!char?._id || char?.level === undefined || char?.level === null) {
        return
      }

      const charId = char._id
      const currentLevel = toNumber(char.level)
      const prevLevel = prevLevels.get(charId)

      // If this is a new character (not in prevLevels), initialize it without showing modal
      if (prevLevel === undefined) {
        currentLevels.set(charId, currentLevel)
        return
      }

      // Store current level
      currentLevels.set(charId, currentLevel)

      // Check for level up (only if level increased)
      if (currentLevel > prevLevel) {
        // Get character name and image
        const characterName = char.characterName || char.data?.characterName || char.characterId || char.data?.characterId || t('levelUpModal.characterAlt')
        const characterImage = char.characterImg || char.data?.characterImg || char.characterImage || char.data?.characterImage

        setLevelUpData({
          type: 'character',
          level: currentLevel,
          characterName: characterName,
          characterImage: characterImage,
          characterId: charId,
        })
        setIsModalOpen(true)
      }
    })

    // Update previous levels (including new characters)
    prevCharacterLevelsRef.current = currentLevels
  }, [userCharacters])

  // Track kingdom level changes
  useEffect(() => {
    // Don't track if not initialized or no empire period data
    if (!isInitializedRef.current || !empirePeriod || empirePeriod.palaceLevel === undefined || empirePeriod.palaceLevel === null) {
      return
    }

    // Don't track if we haven't initialized the previous level yet (first load)
    if (!hasKingdomDataRef.current || prevKingdomLevelRef.current === null) {
      return
    }

    const currentKingdomLevel = toNumber(empirePeriod.palaceLevel) || 1
    const prevKingdomLevel = prevKingdomLevelRef.current

    // First check after initialization: if levels are equal, it's just initialization, not a level up
    if (!hasCheckedKingdomLevelOnceRef.current) {
      hasCheckedKingdomLevelOnceRef.current = true
      // If current equals previous, this is initialization - don't show modal, just sync
      if (currentKingdomLevel === prevKingdomLevel) {
        return // Exit early, don't show modal
      }
      // If they're different on first check, sync and return
      if (currentKingdomLevel !== prevKingdomLevel) {
        prevKingdomLevelRef.current = currentKingdomLevel
        return
      }
    }

    // After first check: only show modal if level actually increased
    if (typeof prevKingdomLevel === 'number' && currentKingdomLevel > prevKingdomLevel) {
      // This is a real level increase - show the modal
      setLevelUpData({
        type: 'kingdom',
        level: currentKingdomLevel,
      })
      setIsModalOpen(true)
      // Update previous level immediately to prevent duplicate triggers
      prevKingdomLevelRef.current = currentKingdomLevel
    } else {
      // Level hasn't changed or decreased - sync the ref silently
      if (currentKingdomLevel !== prevKingdomLevel) {
        prevKingdomLevelRef.current = currentKingdomLevel
      }
    }
  }, [empirePeriod?.palaceLevel])

  const handleCloseModal = () => {
    setIsModalOpen(false)
    // Small delay before clearing data to allow animation to complete
    setTimeout(() => {
      setLevelUpData(null)
    }, 300)
  }

  const handleCloseMilestone = () => {
    // Advance to the next queued milestone (multi-level jump shows both in order).
    setMilestoneQueue((prev) => prev.slice(1))
  }

  const activeMilestone = milestoneQueue[0] ?? null
  const activeReward = rewardQueue[0] ?? null

  /** Drop the claimed level and reset the per-level view state for the next one in the queue. */
  const advanceRewardQueue = useCallback(() => {
    setClaimedRewardLines(null)
    setClaimingReward(false)
    setRewardQueue((prev) => prev.slice(1))
  }, [])

  const handleClaimReward = useCallback(async () => {
    if (!activeReward || !walletAddress || claimingReward) return
    // Reward-less entries (levels past the table) only ever show a dismiss CTA.
    if (activeReward.rewards.length === 0) return
    setClaimingReward(true)
    try {
      const res = await claimUserLevelReward(walletAddress, activeReward.level)
      if (res._httpStatus === 200) {
        // Swap the generic pre-claim descriptions for what the server actually rolled, so the
        // player sees which character / weapon / materials they got before closing.
        if (Array.isArray(res.granted) && res.granted.length > 0) {
          setClaimedRewardLines(res.granted)
        }
        if (walletAddress) {
          dispatch(getResources(walletAddress, null, () => {}) as any)
        }
      } else if (res.alreadyClaimed) {
        // Claimed elsewhere (another tab, or a retried request). Nothing owed — move on.
        advanceRewardQueue()
        return
      }
    } catch {
      /* Non-fatal: the level stays claimable, so the player can retry. */
    } finally {
      setClaimingReward(false)
    }
  }, [activeReward, walletAddress, claimingReward, dispatch, advanceRewardQueue])

  return (
    <>
      {levelUpData && (
        <LevelUpModal
          open={isModalOpen}
          onClose={handleCloseModal}
          type={levelUpData.type}
          level={levelUpData.level}
          characterName={levelUpData.characterName}
          characterImage={levelUpData.characterImage}
          characterId={levelUpData.characterId}
        />
      )}
      {activeMilestone && (
        <MilestoneCelebrationModal
          // Remount per variant so entrance animation replays for the queued milestone
          key={activeMilestone}
          open
          variant={activeMilestone}
          onClose={handleCloseMilestone}
        />
      )}
      {/* Reward claim waits behind any milestone celebration so levels 3 and 5 don't stack
          two modals at once; it reappears on its own once the celebration is dismissed. */}
      {activeReward && !activeMilestone && (
        <LevelRewardModal
          // Remount per level so the entrance animation replays for each queued reward.
          key={`reward-${activeReward.level}`}
          open
          onClose={advanceRewardQueue}
          level={activeReward.level}
          rewards={claimedRewardLines ?? activeReward.rewards}
          onClaim={handleClaimReward}
          claiming={claimingReward}
          claimed={claimedRewardLines !== null}
        />
      )}
    </>
  )
}

export default LevelUpTracker